// FILE: src/pages/attendance/Attendance.jsx
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../../../GlobalContext";
import LoadingScreen from "../../Utils/LoadingScreen";
import "./Attendance.css";
import useVolunteersAllApi from "../../../Hooks/Volunteer Api/useVoluntersAllApi";
import useVolunteerAttendance from "../../../Hooks/useVolunteerAttendance";

const normalizeStatus = (s) => {
  if (!s) return "pending";

  const v = String(s).toLowerCase().trim();

  // Short codes
  if (v === "f") return "full_day";
  if (v === "h") return "half_day";
  if (v === "a") return "absent";
  if (v === "p") return "pending";

  // Full text
  if (v.includes("full")) return "full_day";
  if (v.includes("half")) return "half_day";
  if (v.includes("absent")) return "absent";

  return "pending";
};

const formatWeekDay = (ymd) =>
  new Date(ymd).toLocaleDateString("en-US", { weekday: "short" });

const STATUS_ORDER = ["pending", "full_day", "half_day", "absent"];
const STATUS_LABEL = {
  full_day: "F",
  half_day: "H",
  absent: "A",
  pending: "PN",
};

const STATUS_CLASS = {
  full_day: "bg-green-500 text-white",
  half_day: "bg-yellow-400 text-black",
  absent: "bg-red-500 text-white",
  pending: "bg-blue-500 text-white",
};

const TYPE_BADGE = {
  full_time: "bg-indigo-500/10 text-indigo-700 border-indigo-200",
  part_time: "bg-sky-500/10 text-sky-700 border-sky-200",
  normal: "bg-slate-500/10 text-slate-700 border-slate-200",
};

const toYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

const getMonthDays = (year, month) => {
  const days = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: days }, (_, i) =>
    toYMD(new Date(year, month, i + 1)),
  );
};

const formatDay = (ymd) => new Date(ymd).getDate();

export default function VolunteerAttendance() {
  const { AdminId } = useGlobalContext();
const isAdminReady = !!AdminId;

  const { attendanceData, loading, fetchVolunteerAttendance, updateVolunteerAttendance, } = useVolunteerAttendance();


   const {
      volunteer,
      fetchAllVolunteers,
    } = useVolunteersAllApi();

  // month selection
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-index
  const dates = useMemo(() => getMonthDays(year, month), [year, month]);

  // volunteers list (for dropdown)
 const [volunteerOptions, setVolunteerOptions] = useState([]);
const [volunteerListLoading, setVolunteerListLoading] = useState(false);

  // UI inputs (not applied until Apply Filters)
  const [uivolunteer_code, setUivolunteer_code] = useState("");
  const [uiSearch, setUiSearch] = useState("");
  const [uiType, setUiType] = useState("all");
  const [uiStatus, setUiStatus] = useState("all");

  // applied filters
  const [applied, setApplied] = useState({
  volunteer_code: "",
  search: "",
  type: "all",
  status: "all",
});

  // data
  const [dirtyMap, setDirtyMap] = useState({}); // key = volunteer|date -> {volunteer_code,date,status}
  const [attendanceLoading, setAttendanceLoading] = useState(false);

  const canCallApi = !!AdminId;

  /*  FETCH DRIVER LIST  */
  useEffect(() => {
    // if (!AdminId) return;

    fetchAllVolunteers(AdminId).then((list) => {
  if (Array.isArray(list)) {
    setVolunteerOptions(list);
  }
});
  }, [fetchAllVolunteers, AdminId]);

  /*  APPLY FILTERS  */
 const onApplyFilters = () => {
  const next = {
    volunteer_code: uivolunteer_code,
    search: uiSearch.trim(),
    type: uiType,
    status: uiStatus,
  };

  setApplied(next);

  fetchVolunteerAttendance({
    volunteerCode: next.volunteer_code || "",
    fromDate: dates[0],
    toDate: dates[dates.length - 1],
  });
};


  const onResetFilters = () => {
    setUivolunteer_code("");
    setUiSearch("");
    setUiType("all");
    setUiStatus("all");
    const next = { volunteer_code: "", search: "", type: "all", status: "all" };
    setApplied(next);
    fetchVolunteerAttendance("");
  };

  /*  GRID INTERACTION  */
  const cycleStatus = (volunteerCode, date, current) => {
    const next =
      STATUS_ORDER[(STATUS_ORDER.indexOf(current) + 1) % STATUS_ORDER.length];

    const key = `${volunteerCode}|${date}`;

    setDirtyMap((prev) => ({
      ...prev,
      [key]: { volunteerCode, date, status: next },
    }));
  };

const convertStatusToApi = (status) => {
  if (status === "full_day") return "F";
  if (status === "half_day") return "H";
  if (status === "absent") return "A";
  return "P";
};

const saveAll = async () => {
  console.log("Save button clicked");

  const data = Object.values(dirtyMap).map((item) => ({
    volunteerCode: item.volunteerCode,
    date: item.date,
    status: convertStatusToApi(item.status),
  }));

  console.log("Sending to API:", data);

  if (!data.length) return toast.message("No changes to save");

  const success = await updateVolunteerAttendance(data);

  console.log("API success:", success);

  if (success) {
    setDirtyMap({});
    fetchVolunteerAttendance({
      volunteerCode: uivolunteer_code || "",
      fromDate: dates[0],
      toDate: dates[dates.length - 1],
    });
  }
};


  const discardAll = () => {
    if (!Object.keys(dirtyMap).length) return;
    setDirtyMap({});
    fetchVolunteerAttendance(applied.volunteer_code);
  };

  /*  CLIENT-SIDE FILTERING  */
const filteredVolunteers = useMemo(() => {
  let list = [...attendanceData];

  if (uiType !== "all") {
    list = list.filter(
      (v) => String(v.type || "").toLowerCase() === uiType
    );
  }

  if (uiSearch) {
    const q = uiSearch.toLowerCase();
    list = list.filter((v) => {
      const name = String(v.name || "").toLowerCase().trim();
      const code = String(v.volunteerCode || "").toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }

  if (uiStatus !== "all") {
    list = list.filter((v) =>
      Object.values(v.attendanceMap || {}).some(
        (s) => normalizeStatus(s) === uiStatus
      )
    );
  }

  return list;
}, [attendanceData, uiSearch, uiType, uiStatus]);


  const dirtyCount = Object.keys(dirtyMap).length;

useEffect(() => {
  if (!dates.length) return;

  fetchVolunteerAttendance({
    volunteerCode: uivolunteer_code || "",
    fromDate: dates[0],
    toDate: dates[dates.length - 1],
  });
}, [year, month]);

  return (
    <div className="w-full overflow-x-hidden px-4 py-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Attendance</h1>

          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-600">
            <div className="flex items-center gap-1">
              <span className="att-legend bg-emerald-500" /> F – Full
            </div>
            <div className="flex items-center gap-1">
              <span className="att-legend bg-amber-400" /> H – Half
            </div>
            <div className="flex items-center gap-1">
              <span className="att-legend bg-rose-500" /> A – Absent
            </div>
            <div className="flex items-center gap-1">
              <span className="att-legend bg-indigo-500" /> PN – Pending
            </div>
            <span className="text-slate-400">
              • Click badge to change (F → H → A → PN)
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={discardAll}
            disabled={loading || dirtyCount === 0}
            className="px-3 py-2 rounded-xl border border-rose-200 bg-white text-rose-700 hover:bg-rose-50 disabled:opacity-60"
          >
            Discard ({dirtyCount})
          </button>

          <button
            onClick={saveAll}
            disabled={loading || dirtyCount === 0}
            className="px-3 py-2 rounded-xl bg-green-600 hover:bg-green-700 text-white disabled:opacity-60"
          >
            Save Changes ({dirtyCount})
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-4 bg-white rounded-2xl border border-slate-200 ">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="text-xs text-slate-500">Month</label>
              <input
                type="month"
                value={`${year}-${String(month + 1).padStart(2, "0")}`}
                onChange={(e) => {
                  const [y, m] = e.target.value.split("-");
                  setYear(+y);
                  setMonth(+m - 1);
                }}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200"
              />
            </div>

            <div className="md:col-span-4">
              <label className="text-xs text-slate-500">Volunteer</label>
              <select
                value={uivolunteer_code}
                onChange={(e) => setUivolunteer_code(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white"
                disabled={volunteerListLoading}
              >
                <option value="">All Volunteers</option>
                {volunteerOptions.map((v) => (
                  <option key={v.volunteer_code} value={v.volunteer_code}>
                    {v.fullName || v.username || v.volunteerCode} ({v.volunteer_code})
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-3">
              <label className="text-xs text-slate-500">Search</label>
              <input
                value={uiSearch}
                onChange={(e) => setUiSearch(e.target.value)}
                placeholder="Search name or code..."
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200"
              />
            </div>

            <div className="md:col-span-1">
              <label className="text-xs text-slate-500">Type</label>
              <select
                value={uiType}
                onChange={(e) => setUiType(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white"
              >
                <option value="all">All</option>
                <option value="full_time">Full-time</option>
                <option value="part_time">Part-time</option>
                <option value="normal">Normal</option>
              </select>
            </div>

            <div className="md:col-span-1">
              <label className="text-xs text-slate-500">Status</label>
              <select
                value={uiStatus}
                onChange={(e) => setUiStatus(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white"
              >
                <option value="all">Any</option>
                <option value="full_day">Full</option>
                <option value="half_day">Half</option>
                <option value="absent">Absent</option>
              </select>
            </div>

            <div className="md:col-span-12 flex flex-wrap items-center justify-between gap-2 mt-2">
              <div className="flex gap-2">
                <button
                  onClick={onApplyFilters}
                  disabled={loading || !canCallApi}
                  className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
                >
                  Apply Filters
                </button>

                <button
                  onClick={onResetFilters}
                  disabled={loading || !canCallApi}
                  className="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-60"
                >
                  Reset
                </button>
              </div>

              <div className="text-xs text-slate-500">
                Showing{" "}
                <span className="font-semibold text-slate-700">
                  {filteredVolunteers.length}
                </span>{" "}
                volunteers •{" "}
                <span className="font-semibold text-slate-700">
                  {dates.length}
                </span>{" "}
                days
              </div>
            </div>
          </div>
        </div>
      </div>

      {attendanceLoading ? (
        <div className="mt-4">
          <LoadingScreen />
        </div>
      ) : null}

      {/* Grid */}
      <div className="bg-white rounded-2xl border border-slate-200 mt-4">
        <div className="attendance-grid-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th className="attendance-th sticky-top sticky-driver text-left">
                  Volunteers
                </th>
                {dates.map((d) => (
                <th
  key={d}
  className="bg-orange-400 attendance-th sticky-top text-center date-col"
>
  <div className="flex flex-col items-center leading-tight">
    <span className="font-semibold">
      {formatDay(d)}
    </span>
    <span className="text-[10px] opacity-90">
      {formatWeekDay(d)}
    </span>
  </div>
</th>

                ))}
              </tr>
            </thead>

            <tbody>
              {filteredVolunteers.length === 0 ? (
                <tr>
                  <td
                    colSpan={1 + dates.length}
                    className="py-10 text-center text-slate-500"
                  >
                    No volunteers found for this filter/month.
                  </td>
                </tr>
              ) : null}

              {filteredVolunteers.map((v) => (
                <tr key={v.volunteerCode} className="hover:bg-slate-50/70">
                  <td className=" attendance-td sticky-driver">
                    <div className="min-w-0">
                      <div className="text-[12px] font-semibold text-slate-900 truncate max-w-[150px]">
                        {v.name || "-"}
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {v.volunteerCode}
                      </div>
                      <span
                        className={[
                          "inline-flex items-center mt-1 px-2 py-0.5 rounded-md border text-[10px] font-semibold",
                          TYPE_BADGE[String(v.type || "").toLowerCase()] ||
                            TYPE_BADGE.normal,
                        ].join(" ")}
                      >
                        {String(v.type || "normal").replaceAll("_", " ")}
                      </span>
                    </div>
                  </td>

                  {dates.map((date) => {
                    const key = `${v.volunteerCode}|${date}`;

const rawStatus =
  dirtyMap[key]?.status ||
  v.attendanceMap?.[date];

const status = normalizeStatus(rawStatus);

                    const isDirty = !!dirtyMap[key];

                    return (
                      <td key={date} className=" attendance-td text-center">
                        <button
                          type="button"
                          onClick={() =>
                            cycleStatus(v.volunteerCode, date, status)
                          }
                          className={[
                            "att-badge",
                            STATUS_CLASS[status],
                            isDirty ? "ring-2 ring-slate-900/20" : "",
                          ].join(" ")}
                          title="Click to change"
                        >
                          {STATUS_LABEL[status]}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-slate-500">
            Volunteer column is fixed. Scroll inside grid to see all dates.
          </div>
          <div className="text-xs">
            {dirtyCount ? (
              <span className="text-rose-600 font-semibold">
                {dirtyCount} unsaved change(s)
              </span>
            ) : (
              <span className="text-emerald-600 font-semibold">
                All changes saved
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
